import { Component, OnInit, OnDestroy } from "@angular/core";
import { first } from "rxjs/operators";
import { Subscription } from "rxjs";
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from "@angular/material/legacy-dialog";
import * as _ from "lodash";

import { AuthenticationService, MediaWatcherService, PermitToStayService, UserService } from "../_services";
import { IUser } from "../_models";
import { PermitToStayCheckListItemsDialogComponent } from "./checkListItems";
import { PermitToStayAppointmentsDialogComponent } from "./appointments";
import { AppGlobals } from "../app.globals";

@Component({
    templateUrl: "permitToStay.component.html",
    styleUrls: ["permitToStay.component.css"]
})
export class PermitToStayComponent implements OnInit, OnDestroy {
    studentOrientationPermitsToStay = null;
    isMobile = false;
    today = new Date();

    currentUser: IUser;
    currentUserSubscription: Subscription;
    mediaWatcherSubscription: Subscription;

    allScholasticPeriods = [];

    displayedColumns = [];

    constructor(private authenticationService: AuthenticationService, private userService: UserService, private appGlobals: AppGlobals,
        public dialog: MatDialog, private mediaWatcherService: MediaWatcherService, private permitToStayService: PermitToStayService) {

    }

    ngOnInit() {
        this.appGlobals.pageTitleValue = "Immigration";

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;

            if (this.isMobile)
                this.displayedColumns = ["orientationPermitToStayStudentStatusDescription", "orientationPermitToStayTypeDescription", "permitNumber", "permitStartDate", "permitExpiryDate", "isRenewableOnExpiry", "isRegisteredOnExternalPortal", "checkListItems", "appointments", "payment", "receiptAttachmentFileId", "permitAttachmentFileId"];
            else
                this.displayedColumns = ["statusId", "orientationPermitToStayStudentStatusDescription", "orientationPermitToStayTypeDescription", "permitNumber", "permitStartDate", "permitExpiryDate", "isRenewableOnExpiry", "isRegisteredOnExternalPortal", "checkListItems", "appointments", "payment", "receiptAttachmentFileId", "permitAttachmentFileId"];
        });

        this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
            this.currentUser = user;
        });

        this.getStudentOrientationPermitsToStay();
    }

    getStudentOrientationPermitsToStay() {
        if (this.currentUser) {
            this.permitToStayService.getStudentOrientationPermitsToStay(this.currentUser.studentId)
                .pipe(first())
                .subscribe(result => {
                    let today = new Date();
                    today.setHours(0, 0, 0, 0);

                    this.studentOrientationPermitsToStay = _.orderBy(result, ["dateCreated"], ["desc"]);

                    _.forEach(this.studentOrientationPermitsToStay, (f: any) => {
                        f.statusId = this.getOrientationPermitToStayStudentStatus(f);
                    });

                    //remove the incomplete permit to stay if completed
                    var missingDocuments = this.studentOrientationPermitsToStay.filter(f => f.numRequiredDocuments > 0 && f.canBeModified);

                    if (!missingDocuments || missingDocuments.length === 0) {
                        if (this.currentUser.userNotifications.find(f => f.code === "orientationPermitToStayStudent")) {
                            _.remove(this.currentUser.userNotifications, f => f.code === "orientationPermitToStayStudent");
                        }
                    }
                });
        }
    }

    ngOnDestroy() {
        // unsubscribe to ensure no memory leaks
        this.currentUserSubscription.unsubscribe();
        this.mediaWatcherSubscription.unsubscribe();
    }

    checkListItems(orientationPermitToStayStudentId: number, orientationPermitToStayStudentStatusId: number, funnelId: number, funnel: string, isHousingStudent: boolean, isReturningStudent: boolean, provider: string, providerId?: number) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.width = "90%";
        dialogConfig.height = "auto";
        dialogConfig.panelClass = "popup-dialog-container";

        dialogConfig.data = {
            orientationPermitToStayStudentId: orientationPermitToStayStudentId,
            funnelId: funnelId,
            funnel: funnel,
            isHousingStudent: isHousingStudent,
            isReturningStudent: isReturningStudent,
            provider: provider,
            providerId: providerId,
            canBeModified: orientationPermitToStayStudentStatusId === 1 || orientationPermitToStayStudentStatusId === 2 || orientationPermitToStayStudentStatusId === 3
        };

        const dialogRef = this.dialog.open(PermitToStayCheckListItemsDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(val => {
            if (val) {
                setTimeout(() => {
                    this.getStudentOrientationPermitsToStay();
                }, 500);
            }
        });
    }

    appointments(id: number) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.width = this.isMobile ? "90%" : "55%";
        dialogConfig.height = "auto";
        dialogConfig.panelClass = "popup-dialog-container";

        dialogConfig.data = {
            currentUser: this.currentUser,
            id: id
        };

        this.dialog.open(PermitToStayAppointmentsDialogComponent, dialogConfig);
    }

    openDocument(attachmentId: number, fileType: string) {
        this.userService.openAttachment(attachmentId)
            .pipe(first())
            .subscribe((response: any) => {
                const file = new Blob([response], { type: fileType });
                var fileUrl = URL.createObjectURL(file);
                window.open(fileUrl);
            });
    }

    getOrientationPermitToStayStudentStatus(permitToStayStudent: any) {
        let statusId = 1;

        //completed 
        if (permitToStayStudent.orientationPermitToStayStudentStatusId === 3)
            statusId = 3;
        else {
            //ready for collection
            if (permitToStayStudent.orientationPermitToStayStudentStatusId === 5)
                statusId = 5;
            else {
                //pending
                if (permitToStayStudent.orientationPermitToStayStudentStatusId === 2)
                    statusId = 2;
                else {
                    //ignored
                    if (permitToStayStudent.orientationPermitToStayStudentStatusId === 6)
                        statusId = 6;
                    else {
                        //printed
                        if (permitToStayStudent.orientationPermitToStayStudentStatusId === 4)
                            statusId = 4;
                        else {
                            //collected
                            if (permitToStayStudent.orientationPermitToStayStudentStatusId === 7 || permitToStayStudent.orientationPermitToStayStudentStatusId === 8)
                                statusId = 7;
                        }
                    }
                }
            }
        }

        return statusId;
    }
}
